'use client';

import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useMarkovSignal, useMarkovDiagnostics } from '@/hooks/useMarkov';
import { ArrowUp, ArrowDown, Minus, TrendingUp, Activity } from 'lucide-react';
import type { MarkovSignal } from '@/types/markov.types';

function EVBoundsChart({ signal }: { signal: MarkovSignal }) {
  const { ev_bounds } = signal;
  const range = ev_bounds.ceiling - ev_bounds.floor;
  const floorPercent = 0;
  const meanPercent = ((ev_bounds.mean - ev_bounds.floor) / range) * 100;
  const ceilingPercent = 100;

  return (
    <div className="space-y-3">
      <div className="relative h-16 bg-gradient-to-r from-red-100 via-yellow-100 to-green-100 rounded-lg overflow-hidden">
        {/* Floor marker */}
        <div className="absolute left-0 top-0 h-full w-1 bg-red-600" />
        <div className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-red-700 font-semibold">
          Floor: {ev_bounds.floor.toFixed(4)}
        </div>

        {/* Mean marker */}
        <div
          className="absolute top-0 h-full w-1 bg-blue-600"
          style={{ left: `${meanPercent}%` }}
        />
        <div
          className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 text-xs bg-blue-600 text-white px-2 py-1 rounded font-semibold whitespace-nowrap"
          style={{ left: `${meanPercent}%` }}
        >
          EV: {ev_bounds.mean.toFixed(4)}
        </div>

        {/* Ceiling marker */}
        <div className="absolute right-0 top-0 h-full w-1 bg-green-600" />
        <div className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-green-700 font-semibold">
          Ceiling: {ev_bounds.ceiling.toFixed(4)}
        </div>
      </div>

      <div className="grid grid-cols-3 gap-2 text-sm">
        <div className="text-center">
          <div className="text-red-600 font-semibold">5th Percentile</div>
          <div className="text-muted-foreground">Worst Case</div>
        </div>
        <div className="text-center">
          <div className="text-blue-600 font-semibold">Expected Value</div>
          <div className="text-muted-foreground">Mean Outcome</div>
        </div>
        <div className="text-center">
          <div className="text-green-600 font-semibold">95th Percentile</div>
          <div className="text-muted-foreground">Best Case</div>
        </div>
      </div>
    </div>
  );
}

function SignalCard({ signal }: { signal: MarkovSignal }) {
  const actionColors = {
    buy: 'text-green-600 bg-green-50 border-green-200',
    sell: 'text-red-600 bg-red-50 border-red-200',
    hold: 'text-gray-600 bg-gray-50 border-gray-200',
  };

  const ActionIcon = signal.action === 'buy' ? ArrowUp : signal.action === 'sell' ? ArrowDown : Minus;

  return (
    <Card className={`border-2 ${actionColors[signal.action]}`}>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`p-3 rounded-full ${actionColors[signal.action]}`}>
              <ActionIcon className="w-6 h-6" />
            </div>
            <div>
              <CardTitle className="uppercase">{signal.action}</CardTitle>
              <CardDescription>{signal.instrument}</CardDescription>
            </div>
          </div>
          <div className="text-right">
            <div className="text-2xl font-bold">{signal.contracts}</div>
            <div className="text-sm text-muted-foreground">contracts</div>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Confidence & Risk Approval */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <div className="text-sm text-muted-foreground">Confidence</div>
            <div className="text-xl font-bold">{(signal.confidence * 100).toFixed(1)}%</div>
            <div className="w-full bg-gray-200 rounded-full h-2 mt-1">
              <div
                className="bg-blue-600 h-2 rounded-full transition-all"
                style={{ width: `${signal.confidence * 100}%` }}
              />
            </div>
          </div>
          <div>
            <div className="text-sm text-muted-foreground">Win Probability</div>
            <div className="text-xl font-bold">{(signal.win_probability * 100).toFixed(1)}%</div>
            <div className="w-full bg-gray-200 rounded-full h-2 mt-1">
              <div
                className="bg-green-600 h-2 rounded-full transition-all"
                style={{ width: `${signal.win_probability * 100}%` }}
              />
            </div>
          </div>
        </div>

        {/* EV Bounds Visualization */}
        <div>
          <div className="text-sm font-semibold mb-2 flex items-center gap-2">
            <TrendingUp className="w-4 h-4" />
            Expected Value Bounds (Credible Interval)
          </div>
          <EVBoundsChart signal={signal} />
        </div>

        {/* State Information */}
        <div className="grid grid-cols-2 gap-4 pt-4 border-t">
          <div>
            <div className="text-sm text-muted-foreground">Current State</div>
            <div className="text-lg font-bold">State {signal.current_state}</div>
          </div>
          <div>
            <div className="text-sm text-muted-foreground">Predicted State</div>
            <div className="text-lg font-bold">State {signal.predicted_state}</div>
          </div>
        </div>

        {/* Transition Probability */}
        <div>
          <div className="text-sm text-muted-foreground">Transition Probability</div>
          <div className="text-lg font-bold">{(signal.state_transition_prob * 100).toFixed(1)}%</div>
        </div>

        {/* Regime */}
        <div className="p-3 bg-purple-50 border border-purple-200 rounded-md">
          <div className="flex items-center gap-2 mb-2">
            <Activity className="w-4 h-4 text-purple-600" />
            <span className="font-semibold text-purple-900">Market Regime</span>
          </div>
          <div className="text-lg font-bold text-purple-700">{signal.regime.regime}</div>
          <div className="text-sm text-purple-600">
            Confidence: {(signal.regime.confidence * 100).toFixed(1)}%
          </div>
          <div className="mt-2 flex flex-wrap gap-1">
            {signal.regime.characteristics.map((char, i) => (
              <span key={i} className="px-2 py-1 bg-purple-100 text-purple-800 rounded text-xs">
                {char}
              </span>
            ))}
          </div>
        </div>

        {/* Risk Approval */}
        <div className={`p-3 rounded-md ${signal.risk_approved ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
          <div className="font-semibold">
            {signal.risk_approved ? '✓ Risk Approved' : '✗ Risk Not Approved'}
          </div>
        </div>

        {/* Reasoning */}
        <div>
          <div className="text-sm font-semibold mb-1">AI Reasoning</div>
          <div className="text-sm text-muted-foreground bg-gray-50 p-3 rounded-md">
            {signal.reasoning}
          </div>
        </div>

        {/* Timestamp */}
        <div className="text-xs text-muted-foreground text-right">
          {new Date(signal.timestamp).toLocaleString()}
        </div>
      </CardContent>
    </Card>
  );
}

export default function MarkovSignalsPage() {
  const [instrument, setInstrument] = useState('EUR_USD');
  const [timeframe, setTimeframe] = useState('1H');
  const [enabled, setEnabled] = useState(false);

  const { data: signal, isLoading, error, refetch } = useMarkovSignal(instrument, timeframe, enabled);
  const { data: diagnostics } = useMarkovDiagnostics(instrument, timeframe);

  const handleGetSignal = () => {
    setEnabled(true);
    refetch();
  };

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div>
        <h1 className="text-3xl font-bold">Live Markov Signals</h1>
        <p className="text-muted-foreground mt-2">
          Real-time probabilistic trading signals with expected value bounds and regime detection
        </p>
      </div>

      {/* Controls */}
      <Card>
        <CardHeader>
          <CardTitle>Signal Configuration</CardTitle>
          <CardDescription>Select instrument and timeframe to generate trading signals</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-4 items-end">
            <div className="space-y-2">
              <Label htmlFor="instrument">Instrument</Label>
              <Input
                id="instrument"
                value={instrument}
                onChange={(e) => setInstrument(e.target.value)}
                placeholder="EUR_USD"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="timeframe">Timeframe</Label>
              <Select value={timeframe} onValueChange={setTimeframe}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1M">1 Minute</SelectItem>
                  <SelectItem value="5M">5 Minutes</SelectItem>
                  <SelectItem value="15M">15 Minutes</SelectItem>
                  <SelectItem value="1H">1 Hour</SelectItem>
                  <SelectItem value="4H">4 Hours</SelectItem>
                  <SelectItem value="1D">1 Day</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <Button onClick={handleGetSignal} disabled={isLoading} className="w-full">
              {isLoading ? 'Generating...' : 'Get Signal'}
            </Button>
          </div>

          {error && (
            <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-md">
              <p className="text-red-800">
                Error: {error instanceof Error ? error.message : 'Failed to get signal'}
              </p>
              <p className="text-sm text-red-600 mt-1">
                Make sure the strategy is trained for this instrument/timeframe combination.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Signal Display */}
      {signal && <SignalCard signal={signal} />}

      {/* Diagnostics */}
      {diagnostics && (
        <Card>
          <CardHeader>
            <CardTitle>Strategy Diagnostics</CardTitle>
            <CardDescription>Model health and state distribution</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-3 gap-4">
              <div>
                <div className="text-sm text-muted-foreground">Number of States</div>
                <div className="text-2xl font-bold">{diagnostics.num_states}</div>
              </div>
              <div>
                <div className="text-sm text-muted-foreground">Total Transitions</div>
                <div className="text-2xl font-bold">
                  {diagnostics.transition_matrix.total_transitions.toLocaleString()}
                </div>
              </div>
              <div>
                <div className="text-sm text-muted-foreground">Model Health</div>
                <div className={`text-2xl font-bold ${diagnostics.model_health.is_healthy ? 'text-green-600' : 'text-red-600'}`}>
                  {diagnostics.model_health.is_healthy ? 'Healthy' : 'Warning'}
                </div>
              </div>
            </div>

            {diagnostics.model_health.warnings.length > 0 && (
              <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                <div className="font-semibold text-yellow-900 mb-2">Warnings:</div>
                <ul className="list-disc list-inside text-sm text-yellow-800">
                  {diagnostics.model_health.warnings.map((warning, i) => (
                    <li key={i}>{warning}</li>
                  ))}
                </ul>
              </div>
            )}

            <div>
              <div className="text-sm font-semibold mb-2">State Distribution</div>
              <div className="space-y-2">
                {Object.entries(diagnostics.state_distribution).map(([state, count]) => {
                  const total = Object.values(diagnostics.state_distribution).reduce((a, b) => a + b, 0);
                  const percentage = (count / total) * 100;
                  return (
                    <div key={state}>
                      <div className="flex justify-between text-sm mb-1">
                        <span>{state}</span>
                        <span>{count} ({percentage.toFixed(1)}%)</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-blue-600 h-2 rounded-full transition-all"
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="text-xs text-muted-foreground text-right">
              Last updated: {new Date(diagnostics.model_health.last_updated).toLocaleString()}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
